import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useGetAllLocations } from "@/locations/api/queries/location.queries";
import { useGetMachinesByLocation } from "@/machines/api/queries/machines.queries";
import { useExportData } from "@/export/api/queries/export.queries";
import { IExportInput } from "@/export/api/requests/export.requests";
import { formatDateTime, validateDateRange, getDefaultDates } from "@/export/utils/date-utils";
import { useDateTimeRangeStore } from "@/shared/stores/useDateTimeRangeStore";
import { format } from "date-fns";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

interface ExportForm {
  selectedLocationId: string;
  selectedDevices: string[];
  startDate: Date | undefined;
  endDate: Date | undefined;
  startTime: string;
  endTime: string;
}

export const useExportOperations = () => {
  const { t } = useTranslation("export");
  const [searchParams] = useSearchParams();
  const globalDateTimeRange = useDateTimeRangeStore();
  const [form, setForm] = useState<ExportForm>({
    selectedLocationId: "",
    selectedDevices: [],
    startDate: undefined,
    endDate: undefined,
    startTime: "00:00",
    endTime: "23:59",
  });
  const [urlParamsProcessed, setUrlParamsProcessed] = useState(false);

  const { data: locations, isLoading: locationsLoading } = useGetAllLocations();
  const { data: machines, isLoading: machinesLoading } = useGetMachinesByLocation(
    form.selectedLocationId ? parseInt(form.selectedLocationId) : undefined
  );
  const exportMutation = useExportData();

  // Initialize dates from global date time range
  useEffect(() => {
    const startDate = new Date(globalDateTimeRange.value.fromDate);
    const endDate = new Date(globalDateTimeRange.value.toDate);
    const startTime = format(startDate, "HH:mm");
    const endTime = format(endDate, "HH:mm");

    setForm(prev => ({
      ...prev,
      startDate,
      endDate,
      startTime,
      endTime,
    }));
  }, [globalDateTimeRange.value.fromDate, globalDateTimeRange.value.toDate]);

  // Process URL query parameters for preselection
  useEffect(() => {
    if (urlParamsProcessed || locationsLoading) return;

    const locationParam = searchParams.get('location');
    const machineParam = searchParams.get('machine');

    if (locationParam) {
      setForm(prev => ({
        ...prev,
        selectedLocationId: locationParam,
      }));
      setUrlParamsProcessed(true);
    }
  }, [searchParams, locationsLoading, urlParamsProcessed]);

  // Preselect machines after location is loaded and machines are fetched
  useEffect(() => {
    if (!urlParamsProcessed || machinesLoading || !machines?.data) return;

    const machineParam = searchParams.get('machine');

    if (machineParam && form.selectedLocationId) {
      // Parse machine parameter - can be comma-separated or array format
      const machineIds = machineParam.includes(',')
        ? machineParam.split(',').map(id => id.trim())
        : [machineParam.trim()];

      // Filter to only include valid machine IDs that exist in the current location
      const availableMachineIds = machines.data.map(m => m.device_mxid);
      const validMachineIds = machineIds.filter(id => availableMachineIds.includes(id));

      if (validMachineIds.length > 0) {
        setForm(prev => ({
          ...prev,
          selectedDevices: validMachineIds,
        }));
      }
    }
  }, [machines, machinesLoading, urlParamsProcessed, searchParams, form.selectedLocationId]);

  const handleLocationChange = (locationId: string) => {
    setForm(prev => ({
      ...prev,
      selectedLocationId: locationId,
      selectedDevices: [], // Reset devices when location changes
    }));
  };

  const handleDeviceToggle = (deviceId: string, checked: boolean) => {
    setForm(prev => ({
      ...prev,
      selectedDevices: checked
        ? [...prev.selectedDevices, deviceId]
        : prev.selectedDevices.filter(id => id !== deviceId),
    }));
  };

  const handleSelectAllDevices = () => {
    const allDeviceIds = machines?.data?.map(machine => machine.device_mxid) || [];
    setForm(prev => ({
      ...prev,
      selectedDevices: allDeviceIds,
    }));
  };

  const handleDeselectAllDevices = () => {
    setForm(prev => ({
      ...prev,
      selectedDevices: [],
    }));
  };

  const removeDevice = (deviceId: string) => {
    setForm(prev => ({
      ...prev,
      selectedDevices: prev.selectedDevices.filter(id => id !== deviceId),
    }));
  };

  const handleExport = () => {
    // Validation
    if (!form.selectedLocationId) {
      toast.error(t("toast.selectLocation"));
      return;
    }

    if (form.selectedDevices.length === 0) {
      toast.error(t("toast.selectDevice"));
      return;
    }

    const dateError = validateDateRange(form.startDate, form.endDate);
    if (dateError) {
      toast.error(t(dateError));
      return;
    }

    // Prepare export data
    const exportData: IExportInput = {
      device_mxids: form.selectedDevices.join(','),
      start_time: formatDateTime(form.startDate!, form.startTime),
      end_time: formatDateTime(form.endDate!, form.endTime),
      timezone: "UTC",
    };

    exportMutation.mutate(exportData, {
      onSuccess: () => {
        toast.success(t("toast.exportCompleted"));
      },
      onError: (error) => {
        toast.error(t("toast.failed"));
        console.error("Export error:", error);
      },
    });
  };

  const updateForm = (updates: Partial<ExportForm>) => {
    setForm(prev => ({ ...prev, ...updates }));
  };

  return {
    form,
    locations,
    machines,
    locationsLoading,
    machinesLoading,
    exportMutation,
    handleLocationChange,
    handleDeviceToggle,
    handleSelectAllDevices,
    handleDeselectAllDevices,
    removeDevice,
    handleExport,
    updateForm,
  };
};
